import React, { useState, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Alert, Button, Dialog, useToast } from '../ui';
import { UploadIcon, WarningIcon } from '../ui/icons';
import i18n from '../i18n';

function InventoryImportModal({ onImport, onClose }) {
  const { t } = useTranslation();
  const toast = useToast();
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileContent, setFileContent] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const fileInputRef = useRef(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const files = e.dataTransfer.files;
    if (files && files[0]) {
      handleFileSelection(files[0]);
    }
  };

  const handleFileSelection = (file) => {
    if (!file.name.toLowerCase().endsWith('.json')) {
      toast.error(t('collectionImport.notJson'));
      return;
    }

    setSelectedFile(file);
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = JSON.parse(e.target.result);
        
        // Validate file format
        if (content.type !== 'inventory_full_export') {
          toast.error(i18n.t('collectionImport.invalidFormat'));
          setSelectedFile(null);
          return;
        }
        
        setFileContent(content);
        setPreviewData({
          inventoryName: content.inventory?.name || i18n.t('collectionImport.unknown'),
          itemsCount: content.items_count || 0,
          inventoryType: content.inventory_type?.name || i18n.t('collectionImport.none'),
          locationType: content.location_type?.name || i18n.t('collectionImport.none'),
          exportedAt: content.exported_at
        });
      } catch (error) {
        toast.error(i18n.t('collectionImport.readError'));
        setSelectedFile(null);
      }
    };
    
    reader.readAsText(file);
  };

  const handleFileInput = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileSelection(file);
    }
  };

  const handleImport = async () => {
    if (!fileContent) {
      toast.error(t('collectionImport.selectFirst'));
      return;
    }

    setIsProcessing(true);
    try {
      await onImport(fileContent);
      onClose();
    } catch (error) {
      // Error handling is done in the parent component
      console.error('Import error:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const resetFile = () => {
    setSelectedFile(null);
    setFileContent(null);
    setPreviewData(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Dialog
      open
      onClose={isProcessing ? undefined : onClose}
      title={t('collection.importCollection')}
      size="md"
      footer={(
        <>
          <Button variant="secondary" onClick={onClose} disabled={isProcessing}>{t('common.cancel')}</Button>
          <Button variant="danger" icon={WarningIcon} onClick={handleImport} disabled={!fileContent} loading={isProcessing}>
            {isProcessing ? t('collectionImport.importing') : t('collection.importCollection')}
          </Button>
        </>
      )}
    >
      {!selectedFile ? (
        <>
          <button
            type="button"
            className={`file-drop${dragActive ? ' is-active' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <span className="file-drop__icon"><UploadIcon size={22} /></span>
            <span className="file-drop__title">{t('collectionImport.drop')}</span>
            <span className="file-drop__hint">{t('collectionImport.browse')}</span>
            <span className="file-drop__hint">{t('collectionImport.onlyJson')}</span>
          </button>
          <input ref={fileInputRef} type="file" accept=".json" onChange={handleFileInput} hidden />
        </>
      ) : (
        <div className="dialog-form">
          <Alert tone="success" title={t('collectionImport.loaded')}>
            <dl className="import-summary">
              <dt>{t('collectionImport.file')}</dt><dd>{selectedFile.name}</dd>
              {previewData && (
                <>
                  <dt>{t('collectionImport.collection')}</dt><dd>{previewData.inventoryName}</dd>
                  <dt>{t('collectionImport.items')}</dt><dd>{previewData.itemsCount}</dd>
                  <dt>{t('collectionImport.itemType')}</dt><dd>{previewData.inventoryType}</dd>
                  <dt>{t('collections.locationType')}</dt><dd>{previewData.locationType}</dd>
                  <dt>{t('collectionImport.exported')}</dt><dd>{previewData.exportedAt}</dd>
                </>
              )}
            </dl>
          </Alert>
          <Alert tone="warning" title={t('collectionImport.warning')}>
            <ul className="alert-list">
              <li><Trans i18nKey="collectionImport.note1" components={{ strong: <strong /> }} /></li>
              <li>{t('collectionImport.note2')}</li>
              <li>{t('collectionImport.note3')}</li>
              <li>{t('collectionImport.note4')}</li>
              <li>{t('itemTypeForm.migration.note4')}</li>
            </ul>
          </Alert>
          <Button variant="secondary" onClick={resetFile} disabled={isProcessing} className="import-another">
            {t('collectionImport.chooseAnother')}
          </Button>
        </div>
      )}
    </Dialog>
  );
}

export default InventoryImportModal;